// NotifyPermissionSlide — request notification permission inside the wizard.
//
// Per audit doc 19 §3.2 — permission asked once, in context, with explanation.
//
// Granting permission is not the same as being reachable. The browser only
// delivers once pushManager.subscribe() has run and the backend holds the
// resulting endpoint, so this slide subscribes as well. Asking for permission
// and stopping there — which is what it used to do — leaves a worker who
// pressed "Activează notificările" believing they will be told about jobs, and
// they never are.
//
// This runs inside a real user gesture on purpose. A permission prompt fired on
// page load is the fastest route to a permanent 'denied', which script cannot
// undo.

import { useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { useOnboarding } from '../useOnboarding'
import { subscribeToPush } from '@/services/PushService'
import SlideShell from './SlideShell'

interface Props {
  progress: number
  onBack: () => void
}

export default function NotifyPermissionSlide({ progress, onBack }: Props) {
  const { next, updateData } = useOnboarding()
  const client = useApolloClient()
  const [busy, setBusy] = useState(false)

  const supportsNotifications = typeof window !== 'undefined' && 'Notification' in window

  const handleAllow = async () => {
    if (!supportsNotifications) {
      next()
      return
    }
    setBusy(true)
    try {
      const result = await Notification.requestPermission()
      updateData({ notificationPermission: result })

      if (result === 'granted') {
        // Best-effort. A failure here must not block the wizard: the worker
        // still gets in-app and email notifications, and ensurePushSubscription
        // retries on the next app start.
        await subscribeToPush(client)
      }
    } catch {
      /* permission denied or browser refused */
    } finally {
      setBusy(false)
      next()
    }
  }

  const handleSkip = () => {
    updateData({ notificationPermission: 'default' })
    next()
  }

  return (
    <SlideShell
      progress={progress}
      onBack={onBack}
      footer={
        <div className="flex flex-col gap-2">
          <button
            onClick={handleAllow}
            disabled={busy}
            className="w-full bg-blue-600 active:bg-blue-700 active:scale-[0.99] transition text-white text-base font-medium px-4 py-3.5 rounded-xl disabled:opacity-60"
          >
            {busy ? 'Se solicită…' : 'Activează notificările'}
          </button>
          <button
            onClick={handleSkip}
            className="w-full text-gray-500 text-sm py-2"
          >
            Mai târziu
          </button>
        </div>
      }
    >
      <div className="flex flex-col items-center text-center pt-12">
        <div className="w-20 h-20 rounded-3xl bg-amber-100 flex items-center justify-center mb-6" aria-hidden="true">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Notificări instant
        </h1>
        <p className="text-base text-gray-600 dark:text-gray-300 mt-4 max-w-sm">
          Te anunțăm imediat când o comandă e confirmată, un lucrător ajunge la tine sau apare un mesaj nou.
        </p>
        <ul className="text-sm text-gray-500 mt-6 space-y-1.5 text-left">
          <li>· Confirmare comandă</li>
          <li>· Lucrător sosit / job început</li>
          <li>· Mesaje în chat</li>
          <li>· Plăți primite</li>
        </ul>
      </div>
    </SlideShell>
  )
}
